import { useEffect, useState } from "react";
import { makeStyles, Skeleton, SkeletonItem } from "@fluentui/react-components";
import IAward from "../../../models/award/IAward";
import Hero from "../../../components/hero/Hero";
import { useTranslation } from "react-i18next";
import Award from "./Award";
import AwardModal from "./AwardModal";
import awardService from "../../../services/AwardService";
import Status from "../../../enums/Status";
import { useHeroMainSnap } from "../../../hooks/useHeroMainSnap";

const useStyles = makeStyles({
    mainContainer: {
        padding: 0,
    },
    awards: {
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "center",
        margin: "2rem auto",
        rowGap: "16px",
        columnGap: "48px",
        padding: "32px",
        maxWidth: "100%",

        "@media (min-width: 768px)": {
            maxWidth: "1200px"
        }
    },
    skeleton: {
        width: "100%",
        height: "300px",
        maxWidth: "300px",
    },
    skeletonItem: {
        width: "100%",
        height: "100%"
    }
});

export default function Awards() {
    const { t } = useTranslation("awards");
    const styles = useStyles();

    const [status, setStatus] = useState<Status>(Status.LOADING);
    const [awards, setAwards] = useState<IAward[]>([]);
    const [selectedAward, setSelectedAward] = useState<IAward | null>(null);

    useEffect(() => {
        fetchAwards(setAwards, setStatus);
    }, []);

    // snap behaviour for this page
    useHeroMainSnap("awards-hero", "awards-main", {
        duration: 900,
        easing: "quart",
        heroCapturePx: 80,
        upCapturePx: 300,
        headerOffset: 0
    });

    const openAwardModal = (award: IAward) => {
        setSelectedAward(award);
    };

    const closeAwardModal = () => {
        setSelectedAward(null);
    };

    const showNextAward = () => {
        if (selectedAward) {
            const currentIndex = awards.findIndex(award => award.id === selectedAward.id);
            const nextIndex = (currentIndex + 1) % awards.length;
            setSelectedAward(awards[nextIndex]);
        }
    };

    const showPrevAward = () => {
        if (selectedAward) {
            const currentIndex = awards.findIndex(award => award.id === selectedAward.id);
            const prevIndex = (currentIndex - 1 + awards.length) % awards.length;
            setSelectedAward(awards[prevIndex]);
        }
    };

    return (
        <div className={styles.mainContainer}>
            
            <div id="awards-hero">
                <Hero
                    title={t("hero.title")}
                    subtitle={t("hero.description")}
                    imageUri="/img/banners/awards.webp"
                    />
            </div>

            <div id="awards-main" className={styles.awards}>
                {
                    status === Status.LOADING &&
                    Array.from({ length: 6 }).map((_, index) => (
                        (
                            <Skeleton key={index} className={styles.skeleton}>
                                <SkeletonItem className={styles.skeletonItem} />
                            </Skeleton>
                        )
                    ))
                }
                
                {
                    status === Status.LOAD_SUCCESS &&
                    awards.map((award, index) => <Award key={index} award={award} onClick={openAwardModal} />)
                }
            </div>

            <AwardModal
                award={selectedAward!}
                isOpen={!!selectedAward}
                showNextAward={showNextAward}
                showPrevAward={showPrevAward}
                onDismiss={closeAwardModal}
                />
        </div>
    );
}

async function fetchAwards(
    setAwards: (awards: IAward[]) => void,
    setStatus: (status: Status) => void
) {
    setStatus(Status.LOADING);

    try {
        const awards = await awardService.getAwards();
        
        setAwards(awards);
        setStatus(Status.LOAD_SUCCESS);
    } catch (_) {
        setStatus(Status.LOAD_ERROR);
    }
}